import i18n from '@flatsy/utils/translations'
import { Grid, Typography } from '@material-ui/core'
import { LocationOn as LocationOnIcon } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import parse from 'autosuggest-highlight/parse'
import throttle from 'lodash/throttle'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import FormTextField from '../../TextField'
import GmapsAutoCompleteServiceContext from '../Context'
import { useStyles } from './Style'
import { GoogleMapsAutoCompleteProps } from './Type'

const GooglePlaceAutoComplete: React.FC<GoogleMapsAutoCompleteProps> = ({
  id,
  label,
  initialValue = '',
  helperText,
  error,
  updated,
  onChange,
  testid,
  ...props
}) => {
  const classes = useStyles()
  const [value, setValue] = useState<google.maps.places.AutocompletePrediction | null>(null)
  const [inputValue, setInputValue] = useState<string | null>(null)
  const [options, setOptions] = useState<Array<google.maps.places.AutocompletePrediction>>([])
  const autoCompleteService = useContext(GmapsAutoCompleteServiceContext)
  const fetch = useMemo(
    () =>
      throttle(
        (
          request: google.maps.places.AutocompletionRequest,
          callback: (
            result: google.maps.places.AutocompletePrediction[],
            status: google.maps.places.PlacesServiceStatus
          ) => void
        ) => {
          autoCompleteService?.getPlacePredictions(request, callback)
        },
        200
      ),
    [autoCompleteService]
  )

  useEffect(() => {
    let active = true
    const fetchData = (input: string): void => {
      fetch({ input }, (results) => {
        if (active) {
          let newOptions: Array<google.maps.places.AutocompletePrediction> = []
          if (value) {
            newOptions = [value]
          }
          if (results) {
            newOptions = [...newOptions, ...results]
          }
          inputValue === null && results && setValue(results[0])
          setOptions(newOptions)
        }
      })
    }
    if (!autoCompleteService) {
      return undefined
    }
    if (!inputValue) {
      setOptions(value ? [value] : [])
    }
    if (inputValue) {
      fetchData(inputValue)
    } else if (inputValue === null && initialValue) {
      fetchData(initialValue)
    }
    return () => {
      active = false
    }
  }, [value, inputValue, fetch, initialValue, autoCompleteService])
  return (
    <Autocomplete
      data-testid={testid}
      id={id}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      autoComplete
      includeInputInList
      filterSelectedOptions
      noOptionsText={i18n.t('start_typing_the_address_to_get_suggestions')}
      {...props}
      value={value}
      options={options}
      onChange={(event, newValue, reason) => {
        setOptions(newValue ? [newValue, ...options] : options)
        setValue(newValue)
        onChange && onChange(event, newValue, reason)
      }}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue)
      }}
      renderInput={(params) => (
        <FormTextField
          {...params}
          label={label}
          autoComplete="off"
          updated={updated}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
        />
      )}
      renderOption={(option) => {
        const matches = option.structured_formatting.main_text_matched_substrings
        const parts = parse(
          option.structured_formatting.main_text,
          matches ? matches.map((match) => [match.offset, match.offset + match.length]) : []
        )

        return (
          <Grid container alignItems="center" className="autoComplete_suggestion">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}
              {/* idk */}
              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        )
      }}
    />
  )
}

export default GooglePlaceAutoComplete
